<template>
  <main class="text-mode-page">
    <DangerSignBar :stop-blocked="patientIdBlocksCompletion" />
    <TextModeLayout
        :clinical-note="clinicalNote"
        :questions="questions"
        :question-stage="questionStage"
        :evidence-categories="evidenceCategories"
        :can-complete="canComplete"
        :show-focused-completion-prompt="showFocusedCompletionPrompt"
        :danger-sign-hold="dangerSignHold"
        :has-network-submit-error="hasNetworkSubmitError || hasConfigurationSubmitError"
        :blocked-by-language-alert="showLanguageAlert"
        :show-language-reminder="showLanguageReminder"
        :language-notice-message="languageNoticeMessage"
        @note-input="handleNoteInput"
        @note-submit="handleNoteSubmit"
        @note-resubmit="handleNoteResubmit"
        @mode-change="handleModeChange"
        @question-toggle="handleQuestionToggle"
        @question-answer="handleQuestionAnswer"
        @question-submit="handleQuestionSubmit"
        @revert-evidence-item="handleRevertEvidenceItem"
        @rhs-edit-start="handleRhsEditStarted"
        @rhs-submit="handleRhsSubmit"
        @complete="handleComplete"
        @focused-continue="handleFocusedContinueAssessment"
        @focused-back="handleFocusedBack"
    />
    <language-notice-dialog
      v-if="showLanguageAlert"
      :message="languageNoticeMessage"
      :switch-label="languageNoticeSwitchLabel"
      :continue-label="languageNoticeContinueLabel"
      @continue="handleLanguageNoticeContinue"
      @switch-to-guided="handleLanguageNoticeSwitchToGuided"
    />
    <EvaluationErrorState
        v-if="showEvaluationErrorState"
        @continue="handleNoFocusedContinue"
        @back="handleEmptyEvaluationBack"
    />
    <NetworkErrorState
        v-if="hasNetworkSubmitError"
        @retry="retrySubmit"
        @back="dismissNetworkSubmitError"
    />
    <NetworkErrorState
        v-if="hasConfigurationSubmitError"
        :title="$t('app.assessment_mode.text.configuration_error.title')"
        :message="$t('app.assessment_mode.text.configuration_error.message')"
        @retry="retrySubmit"
        @back="dismissConfigurationSubmitError"
    />
    <DangerSignAlertDialog v-if="showDangerSignAlert" :stop-blocked="patientIdBlocksCompletion" />
  </main>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import TextModeLayout from '../components/TextModeLayout.vue';
import LanguageNoticeDialog from '../components/LanguageNoticeDialog.vue';
import EvaluationErrorState from '../components/EvaluationErrorState.vue';
import NetworkErrorState from '../components/NetworkErrorState.vue';
import DangerSignAlertDialog from '@/features/danger-signs/components/DangerSignAlertDialog.vue';
import DangerSignBar from '@/features/danger-signs/components/DangerSignBar.vue';
import '../styles/text-mode.css';
import { useTextModeWorkflow } from "@/features/text-mode/composables/useTextModeWorkflow";
import { shouldClearContextOnLeave } from '@/features/assessment/config/assessment-mode-exit-routes';
import { i18n } from '@/plugins/i18n';

const LANGUAGE_NOTICE_MESSAGE_TOKEN = 'app.assessment_mode.text.language_notice.message';
const LANGUAGE_NOTICE_SWITCH_TOKEN = 'app.assessment_mode.text.language_notice.switch_to_guided';
const LANGUAGE_NOTICE_CONTINUE_TOKEN = 'app.dialog.button.continue';

const translateLanguageNotice = (token: string): string => {
  const locale = i18n.locale;
  const parentLocale = i18n.fallbackLocale;

  if (i18n.te(token, locale)) {
    return String(i18n.t(token, locale));
  }

  if (typeof parentLocale === 'string' && i18n.te(token, parentLocale)) {
    return String(i18n.t(token, parentLocale));
  }

  return String(i18n.t(token, 'en'));
};

export default defineComponent({
  name: 'TextModePage',

  components: {
    TextModeLayout,
    LanguageNoticeDialog,
    EvaluationErrorState,
    NetworkErrorState,
    DangerSignAlertDialog,
    DangerSignBar,
  },

  beforeRouteLeave(to, _from, next) {
    if (shouldClearContextOnLeave(to)) {
      this.clearTextModeContext();
    }

    next();
  },

  setup() {
    return {
      ...useTextModeWorkflow(),
      languageNoticeMessage: computed(() => translateLanguageNotice(LANGUAGE_NOTICE_MESSAGE_TOKEN)),
      languageNoticeSwitchLabel: computed(() => translateLanguageNotice(LANGUAGE_NOTICE_SWITCH_TOKEN)),
      languageNoticeContinueLabel: computed(() => translateLanguageNotice(LANGUAGE_NOTICE_CONTINUE_TOKEN)),
    };
  },
});
</script>
